import React, { useEffect, useRef, useState } from 'react';
import { Home, Users, CreditCard, Settings, LayoutDashboard, Gift, MessageCircle, PanelLeftClose, PanelLeftOpen, Shield, Menu, X, LogOut } from 'lucide-react';

type AppView = 'overview' | 'properties' | 'dashboard' | 'studio' | 'variants' | 'batch' | 'team' | 'profile' | 'subscription' | 'referrals' | 'feedback';

interface Props {
  view: AppView;
  onNavigate: (view: AppView) => void;
  /** D-07: pass app_role from claims (or fallback user.role) — no bare string comparisons in JSX */
  appRole: 'beheerder' | 'medewerker' | 'superadmin';
  collapsed?: boolean;
  onToggleCollapse?: () => void;
  onLogout?: () => void;
}

const LogoMark: React.FC<{ size?: number }> = ({ size = 22 }) => (
  <svg width={size} height={size} viewBox="0 0 150.015 160.089" fill="currentColor" aria-hidden="true">
    <g transform="translate(-0.014423,160) scale(0.1,-0.1)">
      <path d="M382 1575 c-324 -104 -477 -479 -320 -779 49 -93 156 -195 253 -241 44 -21 81 -39 83 -41 2 -1 -64 -118 -147 -259 l-150 -256 487 3 487 3 60 24 c324 130 457 479 298 783 -48 93 -151 189 -252 236 -42 19 -77 37 -79 38 -2 1 64 117 146 258 l150 256 -471 0 c-469 -1 -472 -1 -545 -25z" />
    </g>
  </svg>
);

export const AppNav: React.FC<Props> = ({ view, onNavigate, appRole, collapsed: desktopCollapsed = false, onToggleCollapse, onLogout }) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const mobileTriggerRef = useRef<HTMLButtonElement>(null);
  // Mobile always shows labels, independently of the desktop collapse preference.
  const collapsed = mobileOpen ? false : desktopCollapsed;
  const closeMobile = () => {
    setMobileOpen(false);
    mobileTriggerRef.current?.focus();
  };
  const navigate = (destination: AppView) => {
    onNavigate(destination);
    if (mobileOpen) closeMobile();
  };

  useEffect(() => {
    if (!mobileOpen) return;
    const close = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setMobileOpen(false);
        mobileTriggerRef.current?.focus();
      }
    };
    document.addEventListener('keydown', close);
    return () => document.removeEventListener('keydown', close);
  }, [mobileOpen]);

  return (
    <>
      <button
        ref={mobileTriggerRef}
        type="button"
        aria-label="Navigatiemenu"
        aria-expanded={mobileOpen}
        aria-controls="app-navigation"
        onClick={() => setMobileOpen(!mobileOpen)}
        className="md:hidden fixed top-2 left-3 z-50 w-11 h-11 rounded-xl bg-ink text-white shadow-lg flex items-center justify-center"
      >
        {mobileOpen ? <X size={20} /> : <Menu size={20} />}
      </button>
      {mobileOpen && (
        <button
          type="button"
          aria-label="Navigatiemenu sluiten"
          onClick={closeMobile}
          className="md:hidden fixed inset-0 z-30 bg-black/30"
        />
      )}
    <aside className={`${mobileOpen ? 'flex flex-col fixed inset-y-0 left-0 z-[45] shadow-xl' : 'hidden'} md:flex md:flex-col md:sticky md:z-auto md:shadow-none shrink-0 bg-white border-r border-black/8 h-screen top-0 transition-[width] duration-200 ease-out ${collapsed ? 'w-[68px]' : 'w-60'}`}>
      <div className="relative flex items-center px-6 pt-5 pb-3">
        <button onClick={() => navigate('overview')} className={`flex items-center gap-2.5 py-1 min-w-0 ${collapsed ? 'justify-center w-full' : 'flex-1'}`} title={collapsed ? 'Stagery' : undefined}>
          <span className="w-9 h-9 rounded-xl bg-accent text-white flex items-center justify-center shadow-[0_4px_12px_-4px_rgba(241,110,0,0.5)] shrink-0">
            <LogoMark size={20} />
          </span>
          {!collapsed && <span className="font-sans text-[17px] font-bold tracking-[-0.02em] text-ink truncate">Stagery</span>}
        </button>
        {onToggleCollapse && !mobileOpen && !collapsed && (
          <button
            onClick={onToggleCollapse}
            className="w-7 h-7 rounded-lg hover:bg-black/5 text-ink/45 hover:text-ink transition-colors grid place-items-center shrink-0 ml-1"
            aria-label="Sidebar inklappen"
            title="Sidebar inklappen"
          >
            <PanelLeftClose size={15} />
          </button>
        )}
      </div>

      {onToggleCollapse && !mobileOpen && collapsed && (
        <button
          onClick={onToggleCollapse}
          className="mx-auto mt-0 mb-2 w-9 h-9 rounded-lg hover:bg-black/5 text-ink/50 hover:text-ink transition-colors grid place-items-center"
          aria-label="Sidebar uitklappen"
          title="Sidebar uitklappen"
        >
          <PanelLeftOpen size={16} />
        </button>
      )}

      <nav id="app-navigation" aria-label={mobileOpen ? 'Mobiele navigatie' : 'Hoofdnavigatie'} className={`flex-1 py-2 flex flex-col gap-0.5 overflow-y-auto ${collapsed ? 'px-2' : 'px-3'}`}>
        {!collapsed && <SideLabel>Werk</SideLabel>}
        <SideItem collapsed={collapsed} active={view === 'overview'} onClick={() => navigate('overview')} icon={<LayoutDashboard size={16} />} label="Overzicht" />
        <SideItem collapsed={collapsed} active={view === 'properties' || view === 'dashboard' || view === 'variants' || view === 'studio'} onClick={() => navigate('properties')} icon={<Home size={16} />} label="Woningen" />
        {(appRole === 'beheerder' || appRole === 'superadmin') && (
          <SideItem collapsed={collapsed} active={view === 'team'} onClick={() => navigate('team')} icon={<Users size={16} />} label="Team" />
        )}

        {!collapsed && <SideLabel className="mt-4">Account</SideLabel>}
        {collapsed && <div className="my-2 mx-2 h-px bg-black/6" />}
        <SideItem collapsed={collapsed} active={view === 'profile'} onClick={() => navigate('profile')} icon={<Settings size={16} />} label="Profiel" />
        <SideItem collapsed={collapsed} active={view === 'referrals'} onClick={() => navigate('referrals')} icon={<Gift size={16} />} label="Referrals" />
        <SideItem collapsed={collapsed} active={view === 'subscription'} onClick={() => navigate('subscription')} icon={<CreditCard size={16} />} label="Abonnement" />
        <SideItem collapsed={collapsed} active={view === 'feedback'} onClick={() => navigate('feedback')} icon={<MessageCircle size={16} />} label="Feedback" />
        {onLogout && (
          <div className="mt-auto pt-3 border-t border-black/6">
            <button
              type="button"
              aria-label="Uitloggen"
              title="Uitloggen"
              onClick={() => {
                if (mobileOpen) closeMobile();
                onLogout();
              }}
              className={`w-full flex items-center rounded-lg text-sm font-medium text-ink/70 hover:bg-black/4 hover:text-ink transition-colors ${collapsed ? 'justify-center px-0 py-2.5' : 'gap-2.5 px-3 py-2'}`}
            >
              <LogOut size={16} aria-hidden="true" />
              {!collapsed && <span>Uitloggen</span>}
            </button>
          </div>
        )}
      </nav>

      {appRole === 'superadmin' && (
        <div className={`border-t border-black/6 ${collapsed ? 'p-2' : 'p-3'}`}>
          <a
            href="/beheerconsole"
            target="_blank"
            rel="noopener noreferrer"
            className={`flex items-center rounded-lg bg-ink text-white font-semibold text-sm shadow-[0_6px_18px_-6px_rgba(0,0,0,0.35)] hover:bg-black hover:shadow-[0_10px_26px_-8px_rgba(0,0,0,0.45)] hover:-translate-y-0.5 transition-all ${collapsed ? 'w-9 h-9 mx-auto grid place-items-center' : 'gap-2.5 px-3 py-2.5'}`}
            aria-label="Open beheerconsole"
            title="Open beheerconsole"
          >
            <Shield size={15} />
            {!collapsed && <span className="flex-1">Open beheerconsole</span>}
          </a>
        </div>
      )}
    </aside>
    </>
  );
};

const SideLabel: React.FC<{ children: React.ReactNode; className?: string }> = ({ children, className = '' }) => (
  <p className={`px-3 pt-2 pb-1 text-[10.5px] font-semibold uppercase tracking-[0.11em] text-ink/45 ${className}`}>{children}</p>
);

const SideItem: React.FC<{
  active: boolean;
  onClick: () => void;
  icon: React.ReactNode;
  label: string;
  collapsed?: boolean;
}> = ({ active, onClick, icon, label, collapsed }) => (
  <button
    onClick={onClick}
    title={collapsed ? label : undefined}
    className={`w-full flex items-center rounded-lg text-sm transition-all ${
      collapsed ? 'justify-center px-0 py-2.5' : 'gap-2.5 px-3 py-2'
    } ${
      active
        ? 'bg-ink/[0.06] text-ink font-semibold'
        : 'text-ink/70 font-medium hover:bg-black/4 hover:text-ink'
    }`}
  >
    <span className={active ? 'text-ink' : 'text-ink/50'}>{icon}</span>
    {!collapsed && label}
  </button>
);
